/* eslint-disable @typescript-eslint/no-explicit-any */
import fetch from 'cross-fetch';
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  useLazyQuery,
  useMutation,
  DocumentNode,
  TypedDocumentNode,
  OperationVariables,
  gql,
  ApolloLink,
  ErrorPolicy,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { split } from '@apollo/client/link/core'; //apollo-link is deprecated and no longer maintained

import {
  CUSTOM_EVENTS_CONSTANTS,
  VITAL_UXL_CALLS,
  VITAL_UXL_CALLS_DTT,
  GQL_OPT_NAME,
  constants,
  GQL_OPT_NAME_DTT,
  FEATURE_FLAG_KEYS
} from '../constants';
import { canUseDOM, getDTTFlag, getProcessEnvs, getPropertyCodeHeaderForChinaLocale, isDev, getPagePropFromWindow} from './helper';
import { isRecipeEnabledInTarget } from '../organisms-mgp/RateCard/helper';
import { eventUtil } from './eventUtil';
import { getComponentNameFromUXL, logBookApplicationError } from './errorLogging';
import { getUXLMigrationStatus, setUXLMigrationStatus } from './migrationHeader';

const { API_SPLIT, API_SPLIT_PARAM, PERSISTENT_CART_FEATURE_FLAG } = constants;

// ============================================================================
// CONFIGURATION & ENVIRONMENT SETUP
// ============================================================================

// Get all environment variables through centralized helper - handles client/server differences
const processenv = getProcessEnvs();

// Safe environment variable extraction - handles test environments gracefully
const getEnvVar = (key: string, fallback: string = '') => {
  return processenv?.[key] || fallback;
};

const NEXT_PUBLIC_GRAPHQL_URL = getEnvVar('NEXT_PUBLIC_GRAPHQL_URL', '/query');
const NEXT_PUBLIC_PREFIX = getEnvVar('NEXT_PUBLIC_PREFIX');
const PROXY_HOST = getEnvVar('PROXY_HOST');
const NEXT_PUBLIC_DIGI_ACCEL_GRAPHQL_URL = getEnvVar('NEXT_PUBLIC_DIGI_ACCEL_GRAPHQL_URL', '/query');
const NEXT_PUBLIC_DIGI_ACCEL_GRAPHQL_PREFIX = getEnvVar('NEXT_PUBLIC_DIGI_ACCEL_GRAPHQL_PREFIX');
const NEXT_PUBLIC_DIGI_ACCEL_PROXY_HOST = getEnvVar('NEXT_PUBLIC_DIGI_ACCEL_PROXY_HOST');

//gql is to be used in components
export { gql };

declare global {
  interface Window {
    graphqlLanguage: {
      graphqlLanguage: string;
    };
  }
}

// ============================================================================
// API SPLITTING CONFIGURATION
// ============================================================================

// Define which operations should use the new endpoint when split is enabled
// Add new operation names here to route them to the new GraphQL service
const NEW_ENDPOINT_OPERATIONS = ['PhoenixBookDTTSearchProductsByPropertyUnified'];

/**
 * Determines whether a specific GraphQL operation should be routed to the new endpoint.
 *
 * @remarks
 * - Only functions in browser environment (requires DOM)
 * - Supports both URL parameter (`?apiSplit=true`) and Adobe Target mbox
 * - Operation must be included in NEW_ENDPOINT_OPERATIONS array
 * - Includes debug logging in development mode
 */
const shouldOperationDigiAccelEndpoint = (operationName: string) => {
  // Server-side rendering safety: cannot check parameters without DOM
  if (!canUseDOM) return false;

  // Disable API splitting during tests to prevent async state update warnings
  if (process.env['NODE_ENV'] === 'test' || process.env['JEST_WORKER_ID']) {
    return false;
  }

  const isSplitEnabled = isRecipeEnabledInTarget(API_SPLIT, API_SPLIT_PARAM);
  if (!isSplitEnabled) return false;
  const shouldUseNew = NEW_ENDPOINT_OPERATIONS.includes(operationName);

  return shouldUseNew;
};

// ============================================================================
// HEADERS & GRAPHQL CONFIGURATION
// ============================================================================

// Default headers for GraphQL requests to original endpoint
// These headers are applied to all operations using the original GraphQL service
const queryHeaders = {
  headers: {
    'accept-language': canUseDOM
      ? window.graphqlLanguage?.graphqlLanguage || process.env['NEXT_PUBLIC_DEFAULT_LANG']
      : process.env['NEXT_PUBLIC_DEFAULT_LANG'],
    'graphql-force-safelisting': [true, constants.TRUE].includes(
      processenv?.['NEXT_PUBLIC_GRAPHQL_FORCE_SAFELISTING'] || ''
    ),
    'apollographql-client-name': 'phoenix_book',
    'apollographql-client-version': 1,
  },
};

// Headers specifically for new endpoint operations
const newEndpointQueryHeaders = {
  headers: {
    ...queryHeaders.headers,
    //'apollographql-client-name': 'phoenix_book',
  },
};

// ============================================================================
// APOLLO LINK CHAIN SETUP
// ============================================================================

// Batched HTTP link for original endpoint - groups multiple operations into single request
// Improves performance by reducing HTTP round trips for compatible operations
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const batchHttpLink = new BatchHttpLink({
  fetch,
  ...queryHeaders,
  uri: operation => {
    // Extract operation name from multiple possible sources with fallback
    const operationName =
      operation.operationName || operation.getContext()['headers']?.['graphql-operation-name'] || 'unknown';
    return constructUri(operationName, false); // false = use original endpoint
  },
});

// Standard HTTP link for original endpoint - handles single operations
// Used when batching is disabled or not supported for specific operations
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const httpLink = new HttpLink({
  fetch,
  ...queryHeaders,
  uri: operation => {
    // Consistent operation name extraction across all links
    const operationName =
      operation.operationName || operation.getContext()['headers']?.['graphql-operation-name'] || 'unknown';
    return constructUri(operationName, false); // false = use original endpoint
  },
});

// HTTP link specifically for new GraphQL endpoint
// Never uses batching - new endpoint handles operations individually for better isolation
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const newHttpLink = new HttpLink({
  fetch,
  ...newEndpointQueryHeaders, // Uses new endpoint specific headers
  uri: operation => {
    // Get operation name and construct new endpoint URI
    const operationName =
      operation.operationName || operation.getContext()['headers']?.['graphql-operation-name'] || 'unknown';
    return constructUri(operationName, true); // true = use new endpoint
  },
});

//this is used to log the errors occured while fetching the query.
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    );
  if (networkError) {
    console.log(`[GraphQL Network error]: ${networkError}`);
  }
});

// Complex endpoint splitting with nested link selection
// This creates a two-level split: first by endpoint (new vs original), then by batching preference
const endpointSplit = split(
  // First-level split: determine which endpoint to use for each operation
  operation => {
    const operationName =
      operation.operationName || operation.getContext()['headers']?.['graphql-operation-name'] || '';

    const shouldUseNew = shouldOperationDigiAccelEndpoint(operationName);

    return shouldUseNew;
  },
  newHttpLink,
  split(
    operation => operation.getContext()['isBatchEnabled'] === true,
    batchHttpLink,
    httpLink
  ) as unknown as ApolloLink
);

// ============================================================================
// APOLLO CLIENT INITIALIZATION
// ============================================================================

export const apolloClient = new ApolloClient({
  link: errorLink.concat(endpointSplit),
  cache: new InMemoryCache(),
});

//method used to set the locale specific languageTag using setContext
export const setGraphqlHeaders = (languageTag?: string) => {
  const updatedLink = setContext((_, previousContext) => ({
    headers: {
      ...previousContext['headers'],
      'accept-language': languageTag || process.env['NEXT_PUBLIC_DEFAULT_LANG'],
    },
  })) as unknown as ApolloLink;
  apolloClient.setLink(updatedLink.concat(errorLink.concat(endpointSplit)));
};

// ============================================================================
// URI CONSTRUCTION UTILITIES
// ============================================================================

const constructUri = (operationName: string, digiAccelEndpoint = false) => {
  //  console.log('🔗 constructUri called with operationName:', operationName, 'digiAccelEndpoint:', digiAccelEndpoint);

  if (digiAccelEndpoint) {
    const newGraphQLUrl = NEXT_PUBLIC_DIGI_ACCEL_GRAPHQL_URL || NEXT_PUBLIC_GRAPHQL_URL;
    const newPrefix = NEXT_PUBLIC_DIGI_ACCEL_GRAPHQL_PREFIX || NEXT_PUBLIC_PREFIX;
    const newProxyHost = NEXT_PUBLIC_DIGI_ACCEL_PROXY_HOST || PROXY_HOST;

    if (isDev) {
      return `${newProxyHost}${newPrefix}${newGraphQLUrl}/${operationName}`;
    }
    return newPrefix ? `${newPrefix}${newGraphQLUrl}/${operationName}` : `${newGraphQLUrl}/${operationName}`;
  }
  if (isDev) {
    return `${PROXY_HOST}${NEXT_PUBLIC_PREFIX}${NEXT_PUBLIC_GRAPHQL_URL}/${operationName}`;
  }
  return NEXT_PUBLIC_PREFIX
    ? `${NEXT_PUBLIC_PREFIX}${NEXT_PUBLIC_GRAPHQL_URL}/${operationName}`
    : `${NEXT_PUBLIC_GRAPHQL_URL}/${operationName}`;
};

// ============================================================================
// CUSTOM APOLLO HOOKS WITH ENHANCED FUNCTIONALITY
// ============================================================================

export const useQueryFetch = (
  COMP_GQL: DocumentNode | TypedDocumentNode<any, OperationVariables>,
  operationName: string,
  tripsXRequestedByHeader?: string | undefined,
  isBatching = false,
  errorPolicy: ErrorPolicy | undefined = 'all',
  additionalHeaders: Record<string, string | boolean> | undefined = {},
  onErrorCustom?: (() => void) | undefined,
  onCompletedCustom?: (() => void) | undefined,
  isSetUXLMigration?: boolean
) => {
  const isDTT = getDTTFlag();
  const isPersistentCartEnabled = getPagePropFromWindow(FEATURE_FLAG_KEYS.ENABLE_PERSISTENT_CART) as boolean;
  // Endpoint routing: determine if this operation should use the new GraphQL service
  const digiAccelEndpoint = shouldOperationDigiAccelEndpoint(operationName);

  // const operationSign = GQL_OPT_SIGN[operationName as keyof typeof GQL_OPT_SIGN];
  return useLazyQuery(COMP_GQL, {
    context: {
      isBatchEnabled: isBatching,
      headers: {
        'graphql-operation-name': `${operationName}`,
        // 'graphql-operation-signature': `${operationSign}`, // Setting the operation signature from the renderer HOC where operationSignatures is passed
        'x-requested-by': tripsXRequestedByHeader ?? '',
        'x-request-id': '', //Setting x-request-id empty to override NextMiApolloClient x-request-id logic
        ...getPropertyCodeHeaderForChinaLocale(),
        ...(isDTT ? { dtt: isDTT } : {}),
        ...(isPersistentCartEnabled ? { [PERSISTENT_CART_FEATURE_FLAG] : isPersistentCartEnabled } : {}),
        // Endpoint-specific headers based on routing decision
        ...(digiAccelEndpoint ? newEndpointQueryHeaders.headers : queryHeaders.headers),
        ...additionalHeaders,
      },
      uri: constructUri(operationName, digiAccelEndpoint),
      ...(isDTT && { getUXLMigrationStatus }),
      ...(isDTT && isSetUXLMigration && { setUXLMigrationStatus }),
    },
    fetchPolicy: 'no-cache',
    errorPolicy: errorPolicy,
    onError: e => {
      const componentName = getComponentNameFromUXL(operationName);
      const VITAL_UXL_CALL = isDTT ? VITAL_UXL_CALLS_DTT : VITAL_UXL_CALLS;
      if (Object.values(VITAL_UXL_CALL).includes(operationName)) {
        logBookApplicationError([
          { errorSource: 'UXL_ERROR', errorCode: operationName, errorComponent: componentName, errorDetail: e.message },
        ]);
        if (
          operationName === GQL_OPT_NAME.propertyPaymentInfo ||
          operationName === GQL_OPT_NAME.customerPaymentInfo ||
          operationName === GQL_OPT_NAME_DTT.propertyPaymentInfo ||
          operationName === GQL_OPT_NAME_DTT.customerPaymentInfo
        ) {
          window.parent.postMessage({ graphQlError: true }, window.location.origin);
        } else {
          eventUtil.dispatch(CUSTOM_EVENTS_CONSTANTS.ON_GRAPHQL_ERROR, {});
        }
      }
      onErrorCustom?.();
    },
    onCompleted: () => {
      onCompletedCustom?.();
    },
  });
};

//custom mutation hook is used to fetch the graphql data
export const useMutationFetch = (
  COMP_GQL: DocumentNode | TypedDocumentNode<any, OperationVariables>,
  operationName: string,
  tripsXRequestedByHeader?: string | undefined,
  additionalHeaders: Record<string, string | boolean> | undefined = {},
  errorPolicy: ErrorPolicy | undefined = 'all'
) => {
  const isDTT = getDTTFlag();
  const isPersistentCartEnabled = getPagePropFromWindow(FEATURE_FLAG_KEYS.ENABLE_PERSISTENT_CART) as boolean;
  const digiAccelEndpoint = shouldOperationDigiAccelEndpoint(operationName);

  return useMutation(COMP_GQL, {
    context: {
      headers: {
        ...(isDTT ? { dtt: isDTT } : {}),
        ...(isPersistentCartEnabled ? { [PERSISTENT_CART_FEATURE_FLAG] : isPersistentCartEnabled } : {}),
        'graphql-operation-name': `${operationName}`,
        // 'graphql-operation-signature': `${operationSign}`,  // Setting the operation signature from the renderer HOC where operationSignatures is passed
        'x-requested-by': tripsXRequestedByHeader ?? '',
        'x-request-id': '', //Setting x-request-id empty to override NextMiApolloClient x-request-id logic
        ...getPropertyCodeHeaderForChinaLocale(),
        // Endpoint-specific headers based on routing decision
        ...(digiAccelEndpoint ? newEndpointQueryHeaders.headers : queryHeaders.headers),
        // Component-provided custom headers
        ...additionalHeaders,
      },
      // Dynamic URI construction for endpoint routing
      uri: constructUri(operationName, digiAccelEndpoint),
      // DTT-specific UXL migration tracking
      ...(isDTT && { getUXLMigrationStatus }),
    },
    errorPolicy: errorPolicy,
    onError: e => {
      const componentName = getComponentNameFromUXL(operationName);
      const VITAL_UXL_CALL = isDTT ? VITAL_UXL_CALLS_DTT : VITAL_UXL_CALLS;
      logBookApplicationError([
        { errorSource: 'UXL_ERROR', errorCode: operationName, errorComponent: componentName, errorDetail: e.message },
      ]);
      if (VITAL_UXL_CALL.includes(operationName)) {
        eventUtil.dispatch(CUSTOM_EVENTS_CONSTANTS.ON_GRAPHQL_ERROR, {});
      }
    },
  });
};
